import moment from 'moment';
import {
	formatDateForParam,
	encodeUTCAdjustedDate,
	numberWithCommas,
	getDefaultDateRanges,
} from '../../utils/DataFormatterUtils';
import {
	ALL_COMPARISON_CHART_KEYS,
	OVERALL_ROW_KEY,
	VIEW_MODE,
	PERFORMER_GROUP,
} from './LifeCycleAnalyticsConstants';
import {
	ComparisonState,
	DEFAULT_COMPARISON_STATE,
	comparisonStateToQueryParams,
	getComparisonCurrentColor,
	queryParamsToComparisonState,
} from '../../utils/AnalyticsComparisonUtils';
import { escapeHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import { changeFilterValueToArrayOfIds, nullSafeGet } from '../../utils/DataAccessUtils';

/** Max calendar span for lifecycle analytics created/completed range filters (see clampDateRange). */
export const LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS = 6;

export const WORK_HOURS_TYPE = {
	WORK_HOURS: 'WORK_HOURS',
	AFTER_HOURS: 'AFTER_HOURS',
} as const;

export const getDefaultFilters = () => ({
	problemTypeIds: [] as number[],
	supplierFacilityIds: [] as number[],
	// Leaf location ids — the region/location tree-select already flattens a selected region into its
	// locations, so the hierarchy never reaches the backend.
	locationIds: [] as number[],
	// `$$`-joined root→leaf area paths, in the exact shape AnalyticsControlBar emits and consumes. The
	// backend resolves them to area ids and expands each id down the area hierarchy.
	areaHierarchyNames: undefined as undefined | string,
	createdAt: null as any,
	completedAt: [
		moment().subtract(LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS, 'months').startOf('day'),
		moment().endOf('day'),
	] as any,
	isPM: undefined as undefined | 'true' | 'false',
	workHoursType: undefined as undefined | 'WORK_HOURS' | 'AFTER_HOURS',
});

export const getLifecycleDateRanges = () => {
	const ranges = getDefaultDateRanges();
	delete ranges['Last 1 year'];
	return ranges;
};

export const clampDateRange = (dates) => {
	if (!dates || !dates[0] || !dates[1]) return dates;
	const start = dates[0].clone();
	let end = dates[1].clone();
	const today = moment().endOf('day');
	if (end.isAfter(today)) {
		end = today;
	}
	const maxEnd = moment.min(
		start.clone().add(LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS, 'months'),
		today
	);
	if (end.isAfter(maxEnd, 'day')) {
		end = maxEnd;
	}
	return [start, end];
};

export const controlBarFiltersToLifecycleFilters = (values) => ({
	problemTypeIds:
		(values.problemTypeIds && changeFilterValueToArrayOfIds(values.problemTypeIds)) || [],
	supplierFacilityIds:
		(values.supplierFacilityIds && changeFilterValueToArrayOfIds(values.supplierFacilityIds)) || [],
	// undefined when "Select All Locations" is on, which means no location filter at all.
	locationIds: (values.locationIds && changeFilterValueToArrayOfIds(values.locationIds)) || [],
	areaHierarchyNames: values.areaHierarchyNames || undefined,
	createdAt:
		values.workCreatedAtStartDate && values.workCreatedAtEndDate
			? clampDateRange([moment(values.workCreatedAtStartDate), moment(values.workCreatedAtEndDate)])
			: null,
	completedAt:
		values.startDate && values.endDate
			? clampDateRange([moment(values.startDate), moment(values.endDate)])
			: null,
	isPM: values.isPM,
});

export const lifecycleFiltersToControlBarValues = (filters) => ({
	startDate: nullSafeGet('completedAt.0', filters),
	endDate: nullSafeGet('completedAt.1', filters),
	workCreatedAtStartDate: nullSafeGet('createdAt.0', filters),
	workCreatedAtEndDate: nullSafeGet('createdAt.1', filters),
	problemTypeIds: filters.problemTypeIds,
	supplierFacilityIds: filters.supplierFacilityIds,
	locationIds: filters.locationIds,
	areaHierarchyNames: filters.areaHierarchyNames,
	isPM: filters.isPM,
});

export const formatSupplierName = (row) => {
	if (row && row.supplierFacilityName) {
		return row.supplierFacilityName;
	}
	return `Supplier #${row.supplierFacilityId}`;
};

export const toMinutes = (seconds) => {
	if (seconds === null || seconds === undefined) {
		return 0;
	}
	return Math.round((seconds / 60) * 100) / 100;
};

export const formatDurationShort = (seconds) => {
	if (seconds === null || seconds === undefined || seconds === 0) {
		return '0m';
	}
	const totalMinutes = Math.floor(seconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const days = Math.floor(hours / 24);
	const remainingHours = hours % 24;
	const remainingMinutes = totalMinutes % 60;

	if (days > 0 && remainingHours > 0) {
		return `${days}d ${remainingHours}h`;
	}
	if (days > 0) {
		return `${days}d`;
	}
	if (hours > 0 && remainingMinutes > 0) {
		return `${hours}h ${remainingMinutes}m`;
	}
	if (hours > 0) {
		return `${hours}h`;
	}
	return `${totalMinutes}m`;
};

export const getPerformerGroupByRank = (index, total) => {
	if (total <= 0) return PERFORMER_GROUP.TYPICAL;

	// For very small numbers, guarantee at least 1 top and 1 bottom (unless total is 1)
	const topCount = Math.max(1, Math.round(total / 3));
	const bottomStartIndex = Math.max(topCount, total - Math.max(1, Math.round(total / 3)));

	if (index < topCount) {
		return PERFORMER_GROUP.TOP;
	}
	if (index >= bottomStartIndex) {
		return PERFORMER_GROUP.BOTTOM;
	}
	return PERFORMER_GROUP.TYPICAL;
};

export const getPositionBasedColor = (index: number, total: number): string => {
	const ratio = total <= 1 ? 1 : index / (total - 1);
	const hue = Math.round(ratio * 120); // 0=red, 60=yellow, 120=green
	return `hsl(${hue}, 80%, 40%)`;
};

export const getPositionBasedBgColor = (index: number, total: number): string => {
	const ratio = total <= 1 ? 1 : index / (total - 1);
	const hue = Math.round(ratio * 120);
	return `hsl(${hue}, 70%, 95%)`;
};

export const filtersToQueryParams = (
	filters,
	selectedPriorityId,
	minWoCount,
	viewMode = VIEW_MODE.ALL
) => {
	const params: any = {};
	if (filters.problemTypeIds && filters.problemTypeIds.length > 0) {
		params.problemTypeIds = filters.problemTypeIds.join(',');
	}
	if (filters.supplierFacilityIds && filters.supplierFacilityIds.length > 0) {
		params.supplierFacilityIds = filters.supplierFacilityIds.join(',');
	}
	if (filters.locationIds && filters.locationIds.length > 0) {
		params.locationIds = filters.locationIds.join(',');
	}
	if (filters.areaHierarchyNames) {
		params.areaHierarchyNames = filters.areaHierarchyNames;
	}
	if (filters.createdAt && filters.createdAt[0] && filters.createdAt[1]) {
		params.createdAtStart = filters.createdAt[0].format('YYYY-MM-DD');
		params.createdAtEnd = filters.createdAt[1].format('YYYY-MM-DD');
	}
	if (filters.completedAt && filters.completedAt[0] && filters.completedAt[1]) {
		params.completedAtStart = filters.completedAt[0].format('YYYY-MM-DD');
		params.completedAtEnd = filters.completedAt[1].format('YYYY-MM-DD');
	}
	if (selectedPriorityId !== null && selectedPriorityId !== undefined) {
		params.priorityId = selectedPriorityId;
	}
	if (minWoCount > 0) {
		params.minWoCount = minWoCount;
	}
	if (filters.isPM !== undefined) {
		params.isPM = filters.isPM;
	}
	if (filters.workHoursType !== undefined) {
		params.workHoursType = filters.workHoursType;
	}
	return params;
};

export const queryParamsToFilters = (queryParams) => {
	const filters = getDefaultFilters();
	if (queryParams.problemTypeIds) {
		filters.problemTypeIds = String(queryParams.problemTypeIds)
			.split(',')
			.map((id) => parseInt(id, 10))
			.filter((id) => !isNaN(id));
	}
	if (queryParams.supplierFacilityIds) {
		filters.supplierFacilityIds = String(queryParams.supplierFacilityIds)
			.split(',')
			.map((id) => parseInt(id, 10))
			.filter((id) => !isNaN(id));
	}
	if (queryParams.locationIds) {
		filters.locationIds = String(queryParams.locationIds)
			.split(',')
			.map((id) => parseInt(id, 10))
			.filter((id) => !isNaN(id));
	}
	if (queryParams.areaHierarchyNames) {
		filters.areaHierarchyNames = String(queryParams.areaHierarchyNames);
	}
	if (queryParams.createdAtStart && queryParams.createdAtEnd) {
		filters.createdAt = [
			moment(String(queryParams.createdAtStart), 'YYYY-MM-DD').startOf('day'),
			moment(String(queryParams.createdAtEnd), 'YYYY-MM-DD').endOf('day'),
		];
	}
	if (queryParams.completedAtStart && queryParams.completedAtEnd) {
		filters.completedAt = [
			moment(String(queryParams.completedAtStart), 'YYYY-MM-DD').startOf('day'),
			moment(String(queryParams.completedAtEnd), 'YYYY-MM-DD').endOf('day'),
		];
	}
	if (queryParams.isPM === 'true' || queryParams.isPM === 'false') {
		filters.isPM = queryParams.isPM;
	}
	if (
		queryParams.workHoursType === WORK_HOURS_TYPE.WORK_HOURS ||
		queryParams.workHoursType === WORK_HOURS_TYPE.AFTER_HOURS
	) {
		filters.workHoursType = queryParams.workHoursType;
	}
	return filters;
};

export {
	escapeHtml,
	buildSeriesComparisonTooltipHtml,
} from '../../utils/AnalyticsComparisonTooltipUtils';

export const buildSupplierTooltipHtml = (name, woCount, responseSeconds, resolutionSeconds) => {
	return (
		'<div style="min-width:240px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">' +
		'<div style="display:flex;justify-content:space-between;align-items:center;padding:0px 14px 8px;border-bottom:1px solid #f0f0f0;">' +
		'<span style="font-weight:500;font-size:14px;color:#757575;">' +
		escapeHtml(name) +
		'</span>' +
		'<span style="font-size:12px;color:#00000073;margin-left:24px;white-space:nowrap;">' +
		numberWithCommas(woCount) +
		' WOs</span>' +
		'</div>' +
		'<div style="padding:8px 14px;">' +
		'<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">' +
		'<span style="font-size:12px;color:#757575;">Avg response time</span>' +
		'<span style="font-size:12px;color:#757575;margin-left:24px;white-space:nowrap;">' +
		formatDurationShort(responseSeconds) +
		'</span>' +
		'</div>' +
		'<div style="display:flex;justify-content:space-between;align-items:center;">' +
		'<span style="font-size:12px;color:#757575;">Avg resolution time</span>' +
		'<span style="font-size:12px;color:#757575;margin-left:24px;white-space:nowrap;">' +
		formatDurationShort(resolutionSeconds) +
		'</span>' +
		'</div>' +
		'</div>' +
		'</div>'
	);
};

export const buildSpendTooltipHtml = (monthTitle, formattedSpend, detailUrl, seriesColor?) => {
	const safeUrl = escapeHtml(detailUrl || '#');
	const accentColor = escapeHtml(seriesColor || getComparisonCurrentColor());
	return (
		'<div style="min-width:160px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">' +
		'<div style="padding:0 0 8px 0;">' +
		'<span style="display:inline-block;width:8px;height:8px;background:' +
		accentColor +
		';margin-right:6px;border-radius:2px;vertical-align:baseline;"></span>' +
		'<span style="font-weight:500;font-size:13px;color:#757575;">' +
		escapeHtml(monthTitle) +
		'</span>' +
		'</div>' +
		'<div style="height:1px;background:#d9d9d9;margin:0 0 8px 0;"></div>' +
		'<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">' +
		'<span style="font-size:12px;color:#757575;font-weight:400;">Spend</span>' +
		'<span style="font-size:12px;color:#757575;font-weight:500;white-space:nowrap;">' +
		escapeHtml(formattedSpend) +
		'</span>' +
		'</div>' +
		'<a href="' +
		safeUrl +
		'" style="font-size:12px;color:' +
		accentColor +
		';font-weight:400;text-decoration:none;cursor:pointer;">view details &rarr;</a>' +
		'</div>'
	);
};

export const buildMetricTooltipHtml = (monthTitle, metricLabel, formattedValue, seriesColor?) => {
	const accentColor = escapeHtml(seriesColor || getComparisonCurrentColor());
	return (
		'<div style="min-width:200px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">' +
		'<div style="padding:0 14px 8px;border-bottom:1px solid #f0f0f0;">' +
		'<span style="font-weight:500;font-size:14px;color:#757575;">' +
		escapeHtml(monthTitle) +
		'</span>' +
		'</div>' +
		'<div style="display:flex;justify-content:space-between;align-items:center;padding:8px 14px;">' +
		'<span style="display:inline-flex;align-items:center;font-size:12px;color:#757575;">' +
		'<span style="display:inline-block;width:8px;height:8px;background:' +
		accentColor +
		';margin-right:6px;border-radius:50%;"></span>' +
		escapeHtml(metricLabel) +
		'</span>' +
		'<span style="font-size:12px;color:#757575;margin-left:24px;white-space:nowrap;">' +
		escapeHtml(formattedValue) +
		'</span>' +
		'</div>' +
		'</div>'
	);
};

export const filtersToParams = (filters) => {
	const params: any = {};
	if (filters.problemTypeIds && filters.problemTypeIds.length > 0) {
		params.problemTypeIds = filters.problemTypeIds.join(',');
	}
	if (filters.supplierFacilityIds && filters.supplierFacilityIds.length > 0) {
		params.supplierFacilityIds = filters.supplierFacilityIds.join(',');
	}
	if (filters.locationIds && filters.locationIds.length > 0) {
		params.locationIds = filters.locationIds.join(',');
	}
	if (filters.areaHierarchyNames) {
		params.areaHierarchyNames = filters.areaHierarchyNames;
	}
	if (filters.createdAt && filters.createdAt[0] && filters.createdAt[1]) {
		params.createdAtStartDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.createdAt[0], false)
		);
		params.createdAtEndDate = formatDateForParam(encodeUTCAdjustedDate(filters.createdAt[1], true));
	}
	if (filters.completedAt && filters.completedAt[0] && filters.completedAt[1]) {
		params.completedAtStartDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.completedAt[0], false)
		);
		params.completedAtEndDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.completedAt[1], true)
		);
	}
	if (filters.isPM !== undefined) {
		params.isPM = filters.isPM;
	}
	if (filters.workHoursType !== undefined) {
		params.workHoursType = filters.workHoursType;
	}
	return params;
};

export const parseSafeInt = (value: any, fallback: any = 0) => {
	if (value === null || value === undefined || value === '') return fallback;
	const parsed = parseInt(String(value), 10);
	return isNaN(parsed) ? fallback : parsed;
};

export const isOverallPriorityId = (priorityId: any) => priorityId === OVERALL_ROW_KEY;

export const parsePriorityIdParam = (value: any, fallback: any = null) => {
	if (isOverallPriorityId(value)) return OVERALL_ROW_KEY;
	return parseSafeInt(value, fallback);
};

export const parseSafeFloat = (value: any, fallback: any = 0) => {
	if (value === null || value === undefined || value === '') return fallback;
	const parsed = parseFloat(String(value));
	return isNaN(parsed) ? fallback : parsed;
};

/**
 * Rounds to one decimal for display-style metrics (percentages, chart labels).
 * Uses toFixed(1) + parseFloat; same caveats as any binary float rounding at edge cases.
 */
export const roundToOneDecimalPlace = (value: number): number => {
	if (value === null || value === undefined) {
		return 0;
	}
	const num = Number(value);
	if (isNaN(num)) {
		return 0;
	}
	return parseFloat(num.toFixed(1));
};

export const extractSpendValue = (item) => {
	if (item === null || item === undefined) {
		return 0;
	}
	const raw =
		item.spend != null
			? item.spend
			: item.totalSpend != null
			? item.totalSpend
			: item.spendUsd != null
			? item.spendUsd
			: item.facilityCostUsd != null
			? item.facilityCostUsd
			: item.amount != null
			? item.amount
			: 0;
	return parseSafeFloat(raw);
};

/** Build the default {chartKey: ComparisonState} map with every chart set to NONE. */
export const getDefaultChartComparisons = (): Record<string, ComparisonState> => {
	const result: Record<string, ComparisonState> = {};
	ALL_COMPARISON_CHART_KEYS.forEach((key) => {
		result[key] = { ...DEFAULT_COMPARISON_STATE };
	});
	return result;
};

export const chartComparisonsToQueryParams = (
	chartComparisons: Record<string, ComparisonState>
): Record<string, string> => {
	const params: Record<string, string> = {};
	ALL_COMPARISON_CHART_KEYS.forEach((chartKey) => {
		Object.assign(params, comparisonStateToQueryParams(chartKey, chartComparisons[chartKey]));
	});
	return params;
};

export const queryParamsToChartComparisons = (
	queryParams: Record<string, any>
): Record<string, ComparisonState> => {
	const result = getDefaultChartComparisons();
	ALL_COMPARISON_CHART_KEYS.forEach((chartKey) => {
		result[chartKey] = queryParamsToComparisonState(chartKey, queryParams);
	});
	return result;
};

/** Override completedAt range on the filter blob (used for fetching the comparison-shifted period). */
export const filtersWithCompletedAtRange = (
	filters: any,
	range: [moment.Moment, moment.Moment]
): any => ({
	...filters,
	completedAt: [range[0].clone().startOf('day'), range[1].clone().endOf('day')],
});
